import { useState } from "react";
import "./CourtForm.css";
import ImageUploader from "./ImageUploader";
import Countries from "./Countries.js";

const CourtForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    capacity: 0,
    address: {
      line1: "",
      line2: "",
      city: "",
      state: "",
      postcode: "",
      country: "",
    },
    longitude: 0,
    latitude: 0,
    contact: {
      phone: "",
      email: "",
      website: "",
    },
    numCourts: 0,
    courts: [
      {
        courtNumber: 0,
        name: "",
        type: "",
        width: 0,
        length: 0,
        goals: false,
        markings: false,
        spectators: false,
        peakCost: 0,
        offPeakCost: 0,
        sharedWith: [],
      },
    ],
    amenities: {
      cafe: false,
      seating: false,
      vending: false,
      disability: false,
      changing: false,
      showers: false,
      toilets: false,
      gym: false,
      fitnessStudio: false,
      communityRoom: false,
      parking: false,
    },
  });

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      capacity: 0,
      address: {
        line1: '',
        line2: '',
        city: '',
        state: '',
        postcode: '',
        country: ''
      },
      longitude: 0,
      latitude: 0,
      contact: {
        phone: '',
        email: '',
        website: ''
      },
      numCourts: 0,
      courts: [{
        name: '',
        type: '',
        length: '',
        width: '',
        peakCost: '',
        offPeakCost: '',
        goals: false,
        markings: false,
        sharedWith: []
      }],
      amenities: {
        cafe: false,
        seating: false,
        vending: false,
        disability: false,
        changing: false,
        showers: false,
        toilets: false,
        gym: false,
        fitnessStudio: false,
        communityRoom: false,
        parking: false,
      }
    });
    setImages([]); // Reset uploaded images
  };

  const [submitStatus, setSubmitStatus] = useState({
    loading: false,
    success: false,
    error: null
  });

  const [images, setImages] = useState([]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formDataToSend = new FormData();

    // Append regular form data
    Object.entries(formData).forEach(([key, value]) => {
      formDataToSend.append(key, value);
    });

    // Append each image
    images.forEach((image, index) => {
      formDataToSend.append(`images`, image);
    });

    try {
      // 1. Upload images first
      const uploadResponses = await Promise.all(
        images.map(async (image) => {
          const formData = new FormData();
          formData.append("images", image); // Field name must match backend

          const res = await fetch(
            `${process.env.REACT_APP_FUTSAL_DIRECTORY_API_URL}/api/images`,
            {
              method: "POST",
              body: formData,
            }
          );

          const data = await res.json();

          // Return the first image URL from the response
          return data.images?.[0]?.url || null;
        })
      );

      // Filter out any failed uploads
      const imageUrls = uploadResponses.filter((url) => url !== null);

      // Now use imageUrls array for your venue creation
      console.log("Uploaded image URLs:", imageUrls);

      // 2. Send JSON with image URLs

      const response = await fetch(
        `${process.env.REACT_APP_FUTSAL_DIRECTORY_API_URL}/api/venues`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            formData,
            imageUrls, // Use the returned URLs
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      console.log("API Response:", responseData);

      setSubmitStatus({ loading: false, success: true, error: null });
      console.log(submitStatus);
      resetForm();
      // Handle response...
    } catch (error) {
      console.error("Submission error:", error);      
      setSubmitStatus({ loading: false, success: false, error: error.message })
    }
  };

  // Add this near your submit button in the return statement
  const renderStatusMessage = () => {
    if (submitStatus.loading) {
      return <div className="status-message loading">Submitting...</div>;
    }
    if (submitStatus.error) {
      return <div className="status-message error">Error: {submitStatus.error}</div>;
    }
    if (submitStatus.success) {
      return (
        <div className="status-message success">
          Venue submitted successfully!
          <button 
            onClick={() => setSubmitStatus({ loading: false, success: false, error: null })}
            className="close-message"
          >
            ×
          </button>
        </div>
      );
    }
    return null;
  };

  const updateCourtInputs = (num) => {
    const numCourts = parseInt(num, 10);
    if (isNaN(numCourts)) return;

    const updatedCourts = [];
    for (let i = 0; i < numCourts; i++) {
      updatedCourts.push({
        courtNumber: i + 1,
        name: formData.courts[i]?.name || "",
        type: formData.courts[i]?.type || "",
        width: formData.courts[i]?.width || 0,
        length: formData.courts[i]?.length || 0,
        goals: formData.courts[i]?.goals || false,
        markings: formData.courts[i]?.markings || false,
        peakCost: formData.courts[i]?.peakCost || 0,
        offPeakCost: formData.courts[i]?.offPeakCost || 0,
      });
    }

    setFormData((prev) => ({
      ...prev,
      numCourts,
      courts: updatedCourts,
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      address: { ...prev.address, [name]: value },
    }));
  };

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      contact: { ...prev.contact, [name]: value },
    }));
  };

  const handleCourtChange = (index, field, value) => {
    const updatedCourts = [...formData.courts];
    updatedCourts[index] = {
      ...updatedCourts[index],
      [field]:
        field === "width" ||
        field === "length" ||
        field === "peakCost" ||
        field === "offPeakCost"
          ? value === ""
            ? 0
            : parseFloat(value) || 0
          : value,
    };
    setFormData((prev) => ({ ...prev, courts: updatedCourts }));
  };

  const handleSharedCourtChange = (
    currentCourtIndex,
    otherCourtIndex,
    isChecked
  ) => {
    setFormData((prev) => {
      const updatedCourts = [...prev.courts];
      const sharedWith = updatedCourts[currentCourtIndex].sharedWith || [];
      const otherCourtNumber = otherCourtIndex + 1;

      if (isChecked) {
        // Add to sharedWith if not already present
        if (!sharedWith.includes(otherCourtNumber)) {
          updatedCourts[currentCourtIndex].sharedWith = [
            ...sharedWith,
            otherCourtNumber,
          ];
        }
      } else {
        // Remove from sharedWith
        updatedCourts[currentCourtIndex].sharedWith = sharedWith.filter(
          (num) => num !== otherCourtNumber
        );
      }

      return { ...prev, courts: updatedCourts };
    });
  };

  const handleAmenityChange = (amenity) => {
    setFormData((prev) => ({
      ...prev,
      amenities: {
        ...prev.amenities,
        [amenity]: !prev.amenities[amenity],
      },
    }));
  };

  return (
    <div className="form-container">
      <form onSubmit={handleSubmit}>
        {/* Basic Information */}
        <div className="form-section">
          <h2>Venue Information</h2>

          <div className="input_box">
            <label htmlFor="name">Venue Name*</label>
            <input
              type="text"
              id="name"
              name="name"
              placeholder="Enter venue name"
              required
              value={formData.name}
              onChange={handleInputChange}
            />
          </div>

          <div className="input_box">
            <label className="description" htmlFor="description">
              Venue Description
            </label>
            <textarea
              id="description"
              name="description"
              placeholder="Describe your venue"
              rows="4"
              value={formData.description}
              onChange={handleInputChange}
            />
          </div>

          <div className="input_box">
            <label htmlFor="capacity">Venue Capacity</label>
            <input
              type="text"
              id="capacity"
              name="capacity"
              placeholder="Enter venue capacity"
              required
              value={formData.capacity}
              onChange={handleInputChange}
            />
          </div>
        </div>
        {/* Address Section */}
        <div className="form-section">
          <h2>Address</h2>

          <div className="input_box">
            <label htmlFor="address-line1">Address Line 1*</label>
            <input
              type="text"
              id="address-line1"
              name="line1"
              placeholder="Street address"
              required
              value={formData.address.line1}
              onChange={handleAddressChange}
            />
          </div>

          <div className="input_box">
            <label htmlFor="address-line2">Address Line 2</label>
            <input
              type="text"
              id="address-line2"
              name="line2"
              placeholder="Apartment, suite, unit, etc."
              value={formData.address.line2}
              onChange={handleAddressChange}
            />
          </div>

          <div className="address-grid">
            <div className="input_box">
              <label htmlFor="city">City*</label>
              <input
                type="text"
                id="city"
                name="city"
                placeholder="City"
                required
                value={formData.address.city}
                onChange={handleAddressChange}
              />
            </div>

            <div className="input_box">
              <label htmlFor="state">State/Province</label>
              <input
                type="text"
                id="state"
                name="state"
                placeholder="State or province"
                value={formData.address.state}
                onChange={handleAddressChange}
              />
            </div>

            <div className="input_box">
              <label htmlFor="postcode">Postal Code*</label>
              <input
                type="text"
                id="postcode"
                name="postcode"
                placeholder="Postal code"
                required
                value={formData.address.postcode}
                onChange={handleAddressChange}
              />
            </div>

            <div className="input_box">
              <label htmlFor="latitude">Latitude*</label>
              <input
                type="number"
                id="latitude"
                name="latitude"
                placeholder="e.g. 47.6062"
                required
                step="any"
                min={-90}
                max={90}
                value={formData.latitude}
                onChange={handleInputChange}
              />
            </div>

            <div className="input_box">
              <label htmlFor="longitude">Longitude*</label>
              <input
                type="number"
                id="longitude"
                name="longitude"
                placeholder="e.g. 47.6062"
                required
                step="any"
                min={-180}
                max={180}
                value={formData.longitude}
                onChange={handleInputChange}
              />
            </div>

            <div className="input_box">
              <label htmlFor="country">Country*</label>
              <select
                id="country"
                name="country"
                required
                value={formData.address.country}
                onChange={handleAddressChange}
              >
                <option value="">Select Country</option>
                {Countries.map((country) => (
                  <option key={country.code} value={country.name}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="form-section">
          <h2>Contact Information</h2>

          <div className="input_box">
            <label htmlFor="phone">Phone Number</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              placeholder="Enter phone number"
              value={formData.contact.phone}
              onChange={handleContactChange}
            />
          </div>

          <div className="input_box">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              name="email"
              placeholder="Enter email"
              value={formData.contact.email}
              onChange={handleContactChange}
            />
          </div>

          <div className="input_box">
            <label htmlFor="website">Website</label>
            <input
              type="url"
              id="website"
              name="website"
              placeholder="https://example.com"
              value={formData.contact.website}
              onChange={handleContactChange}
            />
          </div>
        </div>

        {/* Court Information */}
        <div className="form-section">
          <h2>Court Information</h2>

          <div className="input_box">
            <label htmlFor="numCourts">Number of Courts*</label>
            <input
              type="number"
              id="numCourts"
              placeholder="Enter number of courts"
              required
              min="1"
              step="1"
              value={formData.numCourts || ""}
              onChange={(e) => updateCourtInputs(e.target.value)}
            />
          </div>

          {formData.courts.map((court, index) => (
            <div key={index} className="court_entry">
              <h3>
                {court.name || `Court ${index + 1}`} {/* Default naming */}
                {court.sharedWith?.length > 0 && (
                  <span className="shared-badge">
                    {" "}
                    (Shared with Court {court.sharedWith.join(", ")})
                  </span>
                )}
              </h3>{" "}
              <div className="court_details">
                <div className="input_box">
                  <label htmlFor={`court_name_${index}`}>Court Name*</label>
                  <input
                    type="text"
                    id={`court_name_${index}`}
                    placeholder="E.g. Main Court, Court 1"
                    required
                    value={court.name || ""}
                    onChange={(e) =>
                      handleCourtChange(index, "name", e.target.value)
                    }
                  />
                </div>

                {/* Add Court Type Dropdown */}
                <div className="input_box">
                  <label htmlFor={`court_type_${index}`}>Court Type*</label>
                  <select
                    id={`court_type_${index}`}
                    required
                    value={court.type || ""}
                    onChange={(e) =>
                      handleCourtChange(index, "type", e.target.value)
                    }
                  >
                    <option value="">Select Court Type</option>
                    <option value="Indoor Hard">Indoor (Hard Surface)</option>
                    <option value="Indoor Soft">Indoor (Soft Surface)</option>
                    <option value="Outdoor Soft">
                      Outdoor (Artificial Turf)
                    </option>
                    <option value="Outdoor Hard">Outdoor (Hard Surface)</option>
                  </select>
                </div>

                <div className="input_box">
                  <label htmlFor={`length_${index}`}>Length (m)*</label>
                  <input
                    type="number"
                    id={`length_${index}`}
                    placeholder="Enter length"
                    required
                    min="0"
                    step="0.1"
                    value={court.length}
                    onChange={(e) =>
                      handleCourtChange(index, "length", e.target.value)
                    }
                  />
                </div>

                <div className="input_box">
                  <label htmlFor={`width_${index}`}>Width (m)*</label>
                  <input
                    type="number"
                    id={`width_${index}`}
                    placeholder="Enter width"
                    required
                    min="0"
                    step="0.1"
                    value={court.width}
                    onChange={(e) =>
                      handleCourtChange(index, "width", e.target.value)
                    }
                  />
                </div>

                <div className="input_box">
                  <label htmlFor={`peakCost_${index}`}>
                    Peak Cost (£/Hour)
                  </label>
                  <input
                    type="number"
                    id={`peakCost_${index}`}
                    placeholder="Enter peak cost"
                    min="0"
                    step="0.01" // Allows decimal values (e.g., £10.50)
                    value={court.peakCost}
                    onChange={(e) =>
                      handleCourtChange(index, "peakCost", e.target.value)
                    }
                  />
                </div>

                <div className="input_box">
                  <label htmlFor={`offPeakCost_${index}`}>
                    Off-Peak Cost (£/Hour)
                  </label>
                  <input
                    type="number"
                    id={`offPeakCost_${index}`}
                    placeholder="Enter off-peak cost"
                    min="0"
                    step="0.01" // Allows decimal values (e.g., £8.50)
                    value={court.offPeakCost}
                    onChange={(e) =>
                      handleCourtChange(index, "offPeakCost", e.target.value)
                    }
                  />
                </div>

                <div className="checkbox_group">
                  <input
                    type="checkbox"
                    id={`goals_${index}`}
                    checked={court.goals}
                    onChange={(e) =>
                      handleCourtChange(index, "goals", e.target.checked)
                    }
                  />
                  <label htmlFor={`goals_${index}`}>Futsal Goals (3x2m)</label>
                </div>

                <div className="checkbox_group">
                  <input
                    type="checkbox"
                    id={`markings_${index}`}
                    checked={court.markings}
                    onChange={(e) =>
                      handleCourtChange(index, "markings", e.target.checked)
                    }
                  />
                  <label htmlFor={`markings_${index}`}>
                    Futsal court markings
                  </label>
                </div>

                {/* Shared Courts Checkboxes (only show if more than 1 court exists) */}
                {formData.courts.length > 1 && (
                  <div className="shared_courts_section">
                    <label>Shares space with:</label>
                    <div className="shared_courts_checkboxes">
                      {formData.courts.map(
                        (otherCourt, otherIndex) =>
                          index !== otherIndex && (
                            <div
                              key={`shared-${index}-${otherIndex}`}
                              className="checkbox_group"
                            >
                              <input
                                type="checkbox"
                                id={`shared_${index}_${otherIndex}`}
                                checked={
                                  court.sharedWith?.includes(otherIndex + 1) ||
                                  false
                                }
                                onChange={(e) =>
                                  handleSharedCourtChange(
                                    index,
                                    otherIndex,
                                    e.target.checked
                                  )
                                }
                              />
                              <label htmlFor={`shared_${index}_${otherIndex}`}>
                                Court {otherIndex + 1}
                              </label>
                            </div>
                          )
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Amenities Section */}
        <div className="form-section">
          <h2>Amenities</h2>
          <div className="amenities-grid">
            {Object.entries(formData.amenities).map(([amenity, checked]) => (
              <div key={amenity} className="checkbox_group">
                <input
                  type="checkbox"
                  id={amenity}
                  checked={checked}
                  onChange={() => handleAmenityChange(amenity)}
                />
                <label htmlFor={amenity}>
                  {amenity
                    .replace(/([A-Z])/g, " $1")
                    .replace(/^./, (str) => str.toUpperCase())}
                </label>
              </div>
            ))}
          </div>
        </div>

        {/* Image Upload */}
        <div className="form-section">
          <h2>Venue Photos</h2>
          <ImageUploader onFilesSelected={setImages} />
        </div>
        {renderStatusMessage()}      
        <button type="submit" className="submit-btn">
        {submitStatus.loading ? 'Submitting...' : 'Submit Venue'}
        </button>
      </form>
    </div>
  );
};

export default CourtForm;
